import type {MutationFunction, QueryFunction} from "@tanstack/react-query";
import {QueryClient} from "@tanstack/react-query";
import type {AxiosRequestConfig} from "axios";
import {queryHttp} from "./queryHttp";

/**
 * Query key shape for the default query function: URL string, then optional Axios config
 * (e.g. params, headers, method, data).
 */
export type UrlQueryKey = readonly [url: string, config?: AxiosRequestConfig];

/**
 * Variables for the default mutation function: required URL plus any Axios fields
 * (defaults to POST if `method` is omitted).
 */
export type UrlMutationVariables = {url: string} & Omit<AxiosRequestConfig, "url">;

export const defaultQueryFn: QueryFunction = async ({queryKey, signal}) => {
    const url = queryKey[0];
    if (typeof url !== "string" || !url) {
        throw new Error("defaultQueryFn: queryKey[0] must be a non-empty URL string");
    }
    const raw = queryKey[1];
    const config: AxiosRequestConfig | undefined =
        raw !== undefined && typeof raw === "object" && !Array.isArray(raw)
            ? (raw as AxiosRequestConfig)
            : undefined;

    const {data} = await queryHttp.request({
        method: "GET",
        url,
        ...config,
        signal,
    });
    return data;
};

export const defaultMutationFn: MutationFunction<unknown, UrlMutationVariables> = async variables => {
    const {url, method = "POST", ...config} = variables;
    if (typeof url !== "string" || !url) {
        throw new Error("defaultMutationFn: variables.url must be a non-empty string");
    }
    const {data} = await queryHttp.request({
        url,
        method,
        ...config,
    });
    return data;
};

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            queryFn: defaultQueryFn,
        },
        mutations: {
            mutationFn: defaultMutationFn as MutationFunction<unknown, unknown>,
        },
    },
});

export {useCustomMutation} from "./useCustomMutation.ts";
export {useCustomQuery} from "./useCustomQuery.ts";
